import { memo } from 'react';
import { AlertTriangle, Info, XCircle } from 'lucide-react';

/**
 * Renders warnings, advisories, overflow, and mustDoReport for a V3 day.
 * Only shows sections that have content.
 */
function DayWarnings({ day }) {
  const { warnings = [], advisories = [], overflow = [], mustDoReport, notices = [] } = day;
  const unscheduled = mustDoReport?.unscheduled || [];
  const hasContent = warnings.length > 0 || advisories.length > 0 || overflow.length > 0 || unscheduled.length > 0;

  if (!hasContent) return null;

  return (
    <div className="mt-3 space-y-2">
      {/* Unscheduled must-dos */}
      {unscheduled.length > 0 && (
        <div className="p-3 rounded-lg border border-amber-200 bg-amber-50">
          <div className="flex gap-2 items-center mb-1.5">
            <AlertTriangle className="w-3.5 h-3.5 text-amber-600" />
            <span className="text-xs font-medium text-amber-800">
              Could not schedule {unscheduled.length} must-do{unscheduled.length > 1 ? 's' : ''}
            </span>
          </div>
          <ul className="space-y-1">
            {unscheduled.map((item, i) => (
              <li key={i} className="flex gap-2 items-start text-xs text-amber-700">
                <XCircle className="flex-shrink-0 mt-0.5 w-3 h-3 text-amber-500" />
                <span>
                  <strong>{item.name}</strong>
                  {item.message && <span className="text-amber-600"> &mdash; {item.message}</span>}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Overflow items */}
      {overflow.length > 0 && (
        <div className="p-3 rounded-lg border border-gray-200 bg-gray-50">
          <div className="flex gap-2 items-center mb-1.5">
            <Info className="w-3.5 h-3.5 text-gray-500" />
            <span className="text-xs font-medium text-gray-700">
              {overflow.length} item{overflow.length > 1 ? 's' : ''} didn't fit in the schedule
            </span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {overflow.map((item, i) => (
              <span key={i} className="px-2 py-0.5 text-[10px] text-gray-600 bg-white rounded-full border border-gray-200">
                {item.name}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Agent warnings */}
      {warnings.length > 0 && (
        <div className="p-3 rounded-lg border border-amber-200 bg-amber-50">
          <ul className="space-y-1">
            {warnings.map((w, i) => (
              <li key={i} className="flex gap-2 items-start text-xs text-amber-700">
                <AlertTriangle className="flex-shrink-0 mt-0.5 w-3 h-3 text-amber-500" />
                <span>{typeof w === 'string' ? w : w.message || JSON.stringify(w)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Advisories */}
      {advisories.length > 0 && (
        <div className="p-3 rounded-lg border border-blue-200 bg-blue-50">
          <ul className="space-y-1">
            {advisories.map((a, i) => (
              <li key={i} className="flex gap-2 items-start text-xs text-blue-700">
                <Info className="flex-shrink-0 mt-0.5 w-3 h-3 text-blue-500" />
                <span>{typeof a === 'string' ? a : a.message || JSON.stringify(a)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default memo(DayWarnings);
